import _ from "lodash";
import React, { useState, useMemo, useEffect, useRef } from "react";

export default function YearPicker({
    label = "",
    isHideLabel = false,
    id = "",
    disabled = false,
    required = false,
    subTitle = "",
    subRequired = false,
    value = null,
    onChange = () => { },
    minYear = 1900,
    maxYear = 2100,
    columns = 4,
    rows = 3,
    placeholder = "Select year",
    className = "",
    errors = null,
    name = "",
    errorName = ""
}) {
    const msg = _.get(errors || {}, errorName || name);
    const yearsPerPage = columns * rows;
    const todayYear = new Date().getFullYear();

    const [open, setOpen] = useState(false);
    const [pageStart, setPageStart] = useState(maxYear - 5);

    // 🔹 Local input state
    const [inputValue, setInputValue] = useState(value ? String(value) : "");

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // 🔹 ALWAYS keep inputValue in sync with parent "value"
    useEffect(() => {
        if (value === null || value === undefined || value === "") {
            setInputValue("");
        } else {
            setInputValue(String(value));
        }
    }, [value]);

    const clamp = (y) => Math.max(minYear, Math.min(maxYear, y));

    const years = useMemo(() => {
        const arr = [];
        for (let i = 0; i < yearsPerPage; i++) {
            arr.push(parseInt(pageStart) + i);
        }
        return arr;
    }, [pageStart, yearsPerPage]);

    const goPrev = () => setPageStart((s) => clamp(s - yearsPerPage));
    const goNext = () => setPageStart((s) => clamp(s + yearsPerPage));

    const handleSelect = (y) => {
        const c = clamp(y);
        // 🔹 Notify parent with the selected year
        onChange(String(c));
        // 🔹 Reflect immediately in the input
        setInputValue(String(c));
        setOpen(false);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === "Enter") {
            const parsed = parseInt(inputValue, 10);
            if (!isNaN(parsed) && parsed >= minYear && parsed <= maxYear) {
                handleSelect(parsed);
            } else {
                inputRef.current?.classList.add("ring-2", "ring-red-400");
                setTimeout(
                    () => inputRef.current?.classList.remove("ring-2", "ring-red-400"),
                    800
                );
            }
        }
    };

    // click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // (Optional) Keep pageStart around selected year when value changes
    useEffect(() => {
        if (!value) return;
        const v = Number(value);
        if (isNaN(v)) return;
        const newStart = clamp(
            Math.max(minYear, Math.min(v - Math.floor(yearsPerPage / 2), maxYear - yearsPerPage + 1))
        );
        setPageStart(newStart);
    }, [value, minYear, maxYear, yearsPerPage]);

    return (
        <div className="w-full font-urbanist">
            {(!!label || !isHideLabel) && (
                <label
                    htmlFor={id}
                    className={`block text-lg font-semibold mb-1 ${disabled ? "text-gray-500" : "text-white"
                        }`}
                >
                    {label} {required && <span className="text-red-600">*</span>}
                </label>
            )}

            {!!subTitle && (
                <p className="text-xs text-gray-500 mb-1">
                    {subTitle}
                    {subRequired && <span className="text-red-600">*</span>}
                </p>
            )}

            <div ref={wrapperRef} className={`relative ${className}`}>
                <div className="relative">
                    <input
                        id={id}
                        name={name}
                        value={inputValue}
                        onChange={(e) =>
                            setInputValue(e.target.value.replace(/[^0-9]/g, ""))
                        }
                        onFocus={() => setOpen(true)}
                        onKeyDown={handleInputKeyDown}
                        placeholder={placeholder}
                        maxLength={4}
                        disabled={disabled}
                        ref={inputRef}
                        className="bg-[#22262E] border border-gray-700 rounded-lg w-full py-2 pl-3 pr-10 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white text-gray-400"
                    />
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                        {/* calendar icon */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                    </button>
                </div>

                {open && (
                    <div className="absolute left-0 mt-2 z-50 w-64 bg-gradient-radial border border-gray-500 bg-[#171A26] rounded-lg shadow-lg p-3">
                        <div className="w-full flex flex-col items-center">
                            <div className="w-full flex items-center justify-between mb-2">
                                <button
                                    onClick={goPrev}
                                    className="px-2 py-1 rounded hover:bg-gray-700 text-white"
                                >
                                    ‹
                                </button>
                                <div className="text-sm font-medium bg-transparent">
                                    {pageStart} — {Math.min(pageStart + yearsPerPage - 1, maxYear)}
                                </div>
                                <button
                                    onClick={goNext}
                                    className="px-2 py-1 rounded hover:bg-gray-700 text-white"
                                >
                                    ›
                                </button>
                            </div>

                            <div className="w-full flex flex-col items-center">
                                <div
                                    className="w-full grid gap-2"
                                    style={{
                                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                                        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`
                                    }}
                                >
                                    {years.map((y) => {
                                        const isDisabled = y < minYear || y > maxYear;
                                        const selected = Number(value) === Number(y);
                                        return (
                                            <button
                                                key={y}
                                                disabled={isDisabled}
                                                onClick={() => handleSelect(y)}
                                                className={`py-2 px-3 w-full rounded text-sm text-center transition-colors ${isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-800 text-white"} ${selected ? "bg-gray-700 text-white font-semibold" : ""}`}
                                            >
                                                {y}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
                                <button
                                    onClick={() => handleSelect(todayYear)}
                                    className="px-3 py-1 rounded hover:bg-gray-700 text-white"
                                >
                                    This year
                                </button>
                                <button
                                    onClick={() => {
                                        setPageStart(minYear);
                                        setInputValue("");
                                        onChange(""); // 🔹 also clear parent if needed
                                    }}
                                    className="px-3 py-1 rounded hover:bg-gray-100"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {!!msg && (
                <div className="text-red-700 text-sm md:text-md mt-2">{msg}</div>
            )}
        </div>
    );
}
